/**
 * Leitura do arquivo de desejo de compra — PURO, sem React e sem rede.
 *
 * O formato é PADRONIZADO: o arquivo traz só **EAN + QTD**. Todo o resto (SKU,
 * código do fornecedor, descrição, marca) sai da NOSSA tabela de produtos, por
 * decisão do dono — é o que torna o arquivo trivial de produzir do lado de
 * fora, onde cada fornecedor tem a própria planilha.
 *
 * Isso não é hipotético: as duas amostras reais chegaram com layouts
 * diferentes — ADIMAX como `Descrição/EAN/Cod.Fornecedor/QTD` e BRF como
 * `SKU/Cód.Forn/EAN/QTD`. Padronizar a entrada foi o que evitou um leitor que
 * adivinha o dialeto de cada fornecedor.
 *
 * Ainda assim a coluna é encontrada pelo NOME, nunca pela posição: a mesma
 * planilha reaberta no Excel volta com as colunas em ordem trocada mais vezes
 * do que se imagina.
 */

/** Rótulos aceitos para cada campo, já normalizados (sem acento/caixa). */
const COLUNAS = {
    ean: ['ean', 'eangtin', 'gtin', 'codigodebarras', 'codbarras', 'barras'],
    quantidade: ['qtd', 'quantidade', 'qtde', 'qty', 'quant'],
};

/** Tira acento, espaço e pontuação para comparar cabeçalho de planilha. */
function normalizarRotulo(valor) {
    return String(valor ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

/**
 * EAN vindo de planilha quase nunca é texto: o Excel entrega `7898752403759`
 * como número, e o SheetJS repassa assim.
 *
 * Truncar e converter é seguro AQUI porque EAN tem no máximo 14 dígitos
 * (99.999.999.999.999), bem abaixo do maior inteiro que um double representa
 * sem perder precisão (~9,007 × 10¹⁵). Não vale para código de barras
 * arbitrário mais longo — se um dia aparecer, o caminho é exigir texto na
 * planilha, não aumentar a conta.
 */
export function NormalizarEan(valor) {
    if (valor === null || valor === undefined) return '';
    if (typeof valor === 'number') {
        if (!Number.isFinite(valor)) return '';
        return String(Math.trunc(valor));
    }
    const texto = String(valor).trim();
    // "7898752403759.0" → "7898752403759"
    const semDecimalZero = texto.replace(/\.0+$/, '');
    return semDecimalZero.replace(/[^\d]/g, '');
}

/** Quantidade: inteiro positivo. Fracionado não existe em pedido de compra. */
export function NormalizarQuantidade(valor) {
    const numero = Number(String(valor ?? '').replace(',', '.'));
    if (!Number.isFinite(numero) || numero <= 0) return 0;
    return Math.floor(numero);
}

/**
 * Acha o índice de cada coluna pelo cabeçalho.
 * @returns {{ean:number, quantidade:number}} índices; -1 quando não achou.
 */
export function MapearColunas(linhaCabecalho = []) {
    const indices = { ean: -1, quantidade: -1 };
    linhaCabecalho.forEach((celula, indice) => {
        const rotulo = normalizarRotulo(celula);
        if (!rotulo) return;
        for (const [campo, aceitos] of Object.entries(COLUNAS)) {
            if (indices[campo] === -1 && aceitos.includes(rotulo)) {
                indices[campo] = indice;
            }
        }
    });
    return indices;
}

/**
 * Converte a matriz da planilha em linhas `{ean, quantidade, linha}`.
 *
 * Procura o cabeçalho nas primeiras linhas em vez de assumir a linha 0: é
 * comum a planilha vir com título ou logo antes da tabela.
 *
 * @param {Array<Array>} matriz linhas da planilha (SheetJS `header: 1`)
 * @returns {{itens: Array, erro: string|null}}
 */
export function LerLinhasDaPlanilha(matriz = []) {
    let indiceCabecalho = -1;
    let colunas = { ean: -1, quantidade: -1 };

    for (let i = 0; i < Math.min(matriz.length, 10); i += 1) {
        const tentativa = MapearColunas(matriz[i] || []);
        if (tentativa.ean >= 0 && tentativa.quantidade >= 0) {
            indiceCabecalho = i;
            colunas = tentativa;
            break;
        }
    }

    if (indiceCabecalho < 0) {
        return {
            itens: [],
            erro: 'Não encontrei as colunas EAN e QTD no arquivo. O cabeçalho precisa ter uma coluna "EAN" e uma "QTD".',
        };
    }

    const itens = [];
    for (let i = indiceCabecalho + 1; i < matriz.length; i += 1) {
        const linha = matriz[i] || [];
        const ean = NormalizarEan(linha[colunas.ean]);
        const quantidade = NormalizarQuantidade(linha[colunas.quantidade]);
        if (!ean && !quantidade) continue; // linha em branco no meio da planilha
        itens.push({ ean, quantidade, linha: i + 1 });
    }

    return { itens, erro: null };
}

/**
 * Cruza as linhas do arquivo com o catálogo e separa o que dá para comprar do
 * que não dá.
 *
 * O que NÃO casou volta explicitamente, com o número da linha. Um EAN
 * desconhecido sumir em silêncio é o pior desfecho possível aqui: o comprador
 * fecharia a OC achando que pediu um item que nunca entrou.
 *
 * @param {Array} itens saída de `LerLinhasDaPlanilha`
 * @param {Map<string, object>} catalogoPorEan produtos indexados por EAN
 */
export function ResolverItensDoArquivo(itens = [], catalogoPorEan = new Map()) {
    const encontrados = [];
    const semProduto = [];
    const invalidos = [];
    const porEan = new Map();

    for (const item of itens) {
        if (!item.ean) {
            invalidos.push({ ...item, motivo: 'linha sem EAN' });
            continue;
        }
        if (item.quantidade <= 0) {
            invalidos.push({ ...item, motivo: 'quantidade ausente ou inválida' });
            continue;
        }
        const produto = catalogoPorEan.get(item.ean);
        if (!produto) {
            semProduto.push(item);
            continue;
        }
        // O mesmo EAN repetido no arquivo SOMA, em vez de virar duas linhas: o
        // comprador quer o total do item, não o histórico de como digitou.
        const acumulado = porEan.get(item.ean);
        if (acumulado) {
            acumulado.quantidade += item.quantidade;
            acumulado.linhas.push(item.linha);
        } else {
            const novo = { ...produto, quantidade: item.quantidade, ean: item.ean, linhas: [item.linha] };
            porEan.set(item.ean, novo);
            encontrados.push(novo);
        }
    }

    return { encontrados, semProduto, invalidos };
}

/** Indexa o catálogo por EAN normalizado (produto sem EAN fica de fora). */
export function IndexarCatalogoPorEan(produtos = []) {
    const porEan = new Map();
    for (const produto of produtos) {
        const ean = NormalizarEan(produto?.ean);
        if (!ean || porEan.has(ean)) continue;
        porEan.set(ean, produto);
    }
    return porEan;
}
